import React, { useState, useEffect } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import { PropaneTank } from "@mui/icons-material";
import { toast } from "react-toastify";
import "../../style/ToastNotifications.css";
import "./CylinderHistoryLayout.css";
import api from "../../api/axiosInstance";
import "./CylinderTrackingHistory.css";

const CylinderHistory = () => {
  const [allCylinders, setAllCylinders] = useState([]);
  const [selectedCylinder, setSelectedCylinder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCylinders, setLoadingCylinders] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [showTravelHistory, setShowTravelHistory] = useState(false);
  const itemsPerPage = 10;
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    fetchAllCylinders();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCylinders = allCylinders.filter((c) => {
    const label = `${c.srNo} ${c.productName} ${c.cylinderCapacity}`.toLowerCase();
    return label.includes(dropdownSearch.toLowerCase());
  });

  const fetchAllCylinders = async () => {
    setLoadingCylinders(true);
    try {
      const response = await api.get("/cylinderMaster/cylinderTrackingBySrNo");
      setAllCylinders(response.data || []);
    } catch (error) {
      console.error("Error fetching cylinders:", error);
      toast.error("Failed to load cylinders");
    } finally {
      setLoadingCylinders(false);
    }
  };

  const handleCylinderSelect = async (cylinder) => {
    setSelectedCylinder(cylinder);
    setLoading(true);
    try {
      const response = await api.get("/cylinderMaster/track", {
        params: {
          productName: cylinder.productName,
          srNo: cylinder.srNo
        }
      });
      setTrackingData(response.data);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching tracking data:", error);
      toast.error("Failed to fetch tracking data");
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "OUT_WITH_CUSTOMER":
        return "status-issued";
      case "IN_STOCK":
        return "status-in-stock";
      case "IN_INVENTORY":
        return "status-in-stock";
      case "RETURNED":
        return "status-returned";
      default:
        return "status-badge";
    }
  };

  const getTransactionTypeBadge = (type) => {
    switch (type) {
      case "ISSUE":
        return "transaction-issue";
      case "RECEIVE":
        return "transaction-receive";
      default:
        return "transaction-badge";
    }
  };

  const travelHistory = trackingData?.travelHistory || [];
  const paginatedData = travelHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(travelHistory.length / itemsPerPage);

  return (
    <div className="cylinder-history-container">
      <div className="cylinder-history-content">
        <div className="cylinder-history">
      {/* Header */}
      <div className="header">
        <div className="cylinder-icon">
          <PropaneTank />
        </div>

        <h2>Cylinder Tracking & History</h2>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-box" ref={dropdownRef}>
          <div className="custom-select">
            <button
              type="button"
              className={`custom-select-trigger ${dropdownOpen ? "open" : ""}`}
              onClick={() => !loadingCylinders && setDropdownOpen((o) => !o)}
              disabled={loadingCylinders}
            >
              <span className={selectedCylinder ? "" : "placeholder"}>
                {selectedCylinder
                  ? `${selectedCylinder.srNo} - ${selectedCylinder.productName} (${selectedCylinder.cylinderCapacity})`
                  : loadingCylinders
                  ? "Loading cylinders..."
                  : "Select a cylinder..."}
              </span>
              <span className="custom-select-arrow" />
            </button>

            {dropdownOpen && (
              <div className="custom-select-panel">
                <input
                  type="text"
                  className="custom-select-search"
                  placeholder="Type to filter..."
                  value={dropdownSearch}
                  onChange={(e) => setDropdownSearch(e.target.value)}
                  autoFocus
                />
                <ul className="custom-select-list">
                  {filteredCylinders.length === 0 ? (
                    <li className="custom-select-empty">No cylinders found</li>
                  ) : (
                    filteredCylinders.map((cylinder) => (
                      <li
                        key={cylinder.id}
                        className={`custom-select-option ${
                          selectedCylinder?.id === cylinder.id ? "selected" : ""
                        }`}
                        onClick={() => {
                          handleCylinderSelect(cylinder);
                          setDropdownOpen(false);
                          setDropdownSearch("");
                        }}
                      >
                        {cylinder.srNo} - {cylinder.productName} ({cylinder.cylinderCapacity})
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Fetching tracking data...</p>
        </div>
      )}

      {/* Current Status Card */}
      {!loading && trackingData && (
        <div className="current-status-card">
          <h3>Current Status</h3>
          <div className="status-grid">
            <div className="status-item">
              <label>Serial Number</label>
              <span>{trackingData.srNo}</span>
            </div>
            <div className="status-item">
              <label>Product</label>
              <span>{trackingData.goodsItem}</span>
            </div>
            <div className="status-item">
              <label>Batch Number</label>
              <span>{trackingData.batchNo}</span>
            </div>
            <div className="status-item">
              <label>Current Location</label>
              <span>{trackingData.currentLocation}</span>
            </div>
            <div className="status-item">
              <label>Current Status</label>
              <span className={`status-badge ${getStatusBadgeClass(trackingData.currentStatus)}`}>
                {trackingData.currentStatus?.replace(/_/g, " ") || "-"}
              </span>
            </div>
            <div className="status-item">
              <label>Current Customer</label>
              <span>{trackingData.currentCustomerName || "-"}</span>
            </div>
            <div className="status-item">
              <label>Current Challan</label>
              <span>{trackingData.currentChallanNumber || "-"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Travel History Card */}
      {!loading && trackingData && travelHistory.length > 0 && (
        <div className="history-section">
          <div className="history-header">
            <h3>Travel History</h3>
            <button
              className="toggle-btn"
              onClick={() => setShowTravelHistory(!showTravelHistory)}
            >
              {showTravelHistory ? (
                <>
                  <FaChevronUp /> Hide
                </>
              ) : (
                <>
                  <FaChevronDown /> Show
                </>
              )}
            </button>
          </div>
          {showTravelHistory && (
            <>
              <div className="table-wrapper">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Challan No</th>
                      <th>Customer</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Created By</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDate(item.transactionDate)}</td>
                        <td>
                          <span className={`transaction-badge ${getTransactionTypeBadge(item.transactionType)}`}>
                            {item.transactionType}
                          </span>
                        </td>
                        <td className="challan-id">{item.challanNumber || "-"}</td>
                        <td>{item.customerName || "-"}</td>
                        <td>{item.currentLocation || "-"}</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(item.currentStatus)}`}>
                            {item.currentStatus?.replace(/_/g, " ") || "-"}
                          </span>
                        </td>
                        <td>{item.createdBy || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <span className="scroll-hint">← Swipe to see more →</span>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft />
                  </button>

                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !trackingData && selectedCylinder && (
        <div className="empty-state">
          <p>No tracking data available for this cylinder</p>
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default CylinderHistory;